import React, { useCallback, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import md5 from "md5";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ReactComponent as LeiPapaIcon } from "../../../assets/icons/header/leiPapa.svg";
import paths from "../../../constants/paths";
import { useAuth } from "../../../hooks/useAuth";

const DRAWER_WIDTH = 280;
const HELP_LINKS = [
  { label: "Docs", href: "https://leipapa.com/docs" },
  { label: "Contact", href: "https://leipapa.com/contact" },
];

const getInitials = (firstName?: string, lastName?: string, email?: string) => {
  const first = firstName?.trim()?.[0];
  const last = lastName?.trim()?.[0];
  if (first || last) {
    return `${first ?? ""}${last ?? ""}`.toUpperCase();
  }
  return email?.trim()?.[0]?.toUpperCase() ?? "?";
};

export const Navbar = () => {
  const { isAuth, setAuth, user } = useAuth();
  const navigate = useNavigate();

  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [helpAnchor, setHelpAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeAccountMenu = () => setAccountAnchor(null);
  const closeHelpMenu = () => setHelpAnchor(null);
  const closeDrawer = () => setDrawerOpen(false);

  const logout = useCallback(() => {
    setAuth(false);
    localStorage.removeItem("token");
    navigate("/");
  }, [setAuth, navigate]);

  const gravatarUrl = useMemo(() => {
    if (!user?.email) return undefined;
    const hash = md5(user.email.trim().toLowerCase());
    return `https://www.gravatar.com/avatar/${hash}?d=404&s=80`;
  }, [user?.email]);

  const initials = getInitials(user?.first_name, user?.last_name, user?.email);

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          borderTop: "4px solid",
          borderImageSource: "linear-gradient(90deg, red, #2B3956, #4B7196, #CFE2F3 75%)",
          borderImageSlice: 1,
          boxShadow: "0 0 1px 0 rgba(128, 128, 128, 0.5)",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            width: "100%",
            maxWidth: 1200,
            mx: "auto",
            // Explicit px, not MUI's spacing shorthand (theme.spacing(4)
            // is 32px, not 4em/64px) - must match the page Container's
            // own 2em/3em/4em breakpoints exactly for the widths to align.
            px: { xs: "32px", sm: "48px", md: "64px" },
            minHeight: "4rem",
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            title="LEIpapa Home"
            sx={{ display: "flex", alignItems: "center", mr: { xs: "auto", md: 3 } }}
          >
            <LeiPapaIcon />
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, flexGrow: 1 }}>
            <Button component={RouterLink} to="/" color="inherit">
              Home
            </Button>
            <Button component={RouterLink} to="/search" color="inherit">
              LEI Search
            </Button>
            {isAuth && (
              <>
                <Button component={RouterLink} to={paths.CARDS_ROUTES.userOrders} color="inherit">
                  Orders
                </Button>
                <Button component={RouterLink} to={paths.CARDS_ROUTES.userRecords} color="inherit">
                  Records
                </Button>
              </>
            )}
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
            <IconButton
              color="inherit"
              onClick={(e) => setHelpAnchor(e.currentTarget)}
              aria-label="Help"
              aria-haspopup="true"
              aria-expanded={!!helpAnchor}
            >
              <HelpOutlineIcon />
            </IconButton>
            <Menu anchorEl={helpAnchor} open={!!helpAnchor} onClose={closeHelpMenu}>
              {HELP_LINKS.map((link) => (
                <MenuItem key={link.label} component="a" href={link.href} target="_blank" rel="noopener noreferrer" onClick={closeHelpMenu}>
                  {link.label}
                </MenuItem>
              ))}
            </Menu>

            {isAuth ? (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 0.5 }}
                  onClick={(e) => setAccountAnchor(e.currentTarget)}
                  aria-haspopup="true"
                  aria-expanded={!!accountAnchor}
                >
                  <Avatar src={gravatarUrl} sx={{ width: 32, height: 32, fontSize: 14 }}>
                    {initials}
                  </Avatar>
                  <KeyboardArrowDownIcon fontSize="small" />
                </Box>
                <Menu anchorEl={accountAnchor} open={!!accountAnchor} onClose={closeAccountMenu}>
                  <MenuItem component={RouterLink} to={paths.SETTINGS} onClick={closeAccountMenu}>
                    My Profile
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeAccountMenu();
                      logout();
                    }}
                  >
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button component={RouterLink} to={paths.AUTHORIZATION} color="inherit" variant="outlined">
                  Log In
                </Button>
                <Button component={RouterLink} to={paths.REGISTRATION} color="primary" variant="contained" disableElevation>
                  Sign Up
                </Button>
              </>
            )}
          </Box>

          <IconButton
            sx={{ display: { xs: "inline-flex", md: "none" } }}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: DRAWER_WIDTH }} role="presentation" onClick={closeDrawer}>
          {isAuth && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}>
              <Avatar src={gravatarUrl} sx={{ width: 40, height: 40 }}>
                {initials}
              </Avatar>
              <Box sx={{ overflow: "hidden" }}>
                <Box sx={{ fontWeight: 600, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                  {user?.first_name || user?.email}
                </Box>
              </Box>
            </Box>
          )}
          <Divider />
          <List>
            <ListItemButton component={RouterLink} to="/">
              <ListItemText primary="Home" />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/search">
              <ListItemText primary="LEI Search" />
            </ListItemButton>
            {isAuth && (
              <>
                <ListItemButton component={RouterLink} to={paths.CARDS_ROUTES.userOrders}>
                  <ListItemText primary="Orders" />
                </ListItemButton>
                <ListItemButton component={RouterLink} to={paths.CARDS_ROUTES.userRecords}>
                  <ListItemText primary="Records" />
                </ListItemButton>
              </>
            )}
          </List>
          <Divider />
          <List>
            {HELP_LINKS.map((link) => (
              <ListItemButton key={link.label} component="a" href={link.href} target="_blank" rel="noopener noreferrer">
                <ListItemText primary={link.label} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <List>
            {isAuth ? (
              <>
                <ListItemButton component={RouterLink} to={paths.SETTINGS}>
                  <ListItemText primary="My Profile" />
                </ListItemButton>
                <ListItemButton onClick={logout}>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </>
            ) : (
              <>
                <ListItemButton component={RouterLink} to={paths.AUTHORIZATION}>
                  <ListItemText primary="Log In" />
                </ListItemButton>
                <ListItemButton component={RouterLink} to={paths.REGISTRATION}>
                  <ListItemText primary="Sign Up" />
                </ListItemButton>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};
